"use client"

import { realtimeManager } from "@/utils/realtime/manager"

type Listener = (state: Record<string, boolean>) => void

class UnreadStore {
  private unreadByRoom: Record<string, boolean> = {}
  private listeners = new Set<Listener>()
  private userId: string | null | undefined
  private inFlight: Promise<void> | null = null
  private lastFetch = 0
  private throttleMs = 1200
  private cleanupRealtime: (() => void) | null = null
  private started = false
  private pendingMarkRead = new Set<string>()

  start(userId?: string | null) {
    if (this.started) {
      this.setUser(userId)
      return
    }
    this.started = true
    this.setUser(userId)
    if (typeof window !== 'undefined') {
      window.addEventListener('storage', this.onStorage)
      window.addEventListener('localStorageUpdate', this.onLocalStorageUpdate as EventListener)
      document.addEventListener('visibilitychange', this.onVisibility)
    }
    if (userId) void this.fetchUnread(true)
  }

  stop() {
    if (!this.started) return
    this.started = false
    if (typeof window !== 'undefined') {
      window.removeEventListener('storage', this.onStorage)
      window.removeEventListener('localStorageUpdate', this.onLocalStorageUpdate as EventListener)
      document.removeEventListener('visibilitychange', this.onVisibility)
    }
    if (this.cleanupRealtime) this.cleanupRealtime()
    this.cleanupRealtime = null
    this.inFlight = null
  }

  subscribe(listener: Listener) {
    this.listeners.add(listener)
    // Push current state immediately
    listener(this.unreadByRoom)
    return () => { this.listeners.delete(listener) }
  }

  setUser(userId?: string | null) {
    if (this.userId === userId) return
    this.userId = userId
    // Rewire realtime subscriptions for the new user
    if (this.cleanupRealtime) this.cleanupRealtime()
    this.cleanupRealtime = null
    if (userId) this.setupRealtime()
    if (userId) void this.fetchUnread(true)
  }

  private async setupRealtime() {
    if (!this.userId) return
    try {
      const res = await fetch('/api/chat/inbox')
      if (!res.ok) return
      const json = await res.json()
      const list = (json.conversations || []) as Array<{ room_name: string }>
      const names = Array.from(new Set(list.map(r => this.canonicalize(r.room_name))))
      this.cleanupRealtime = realtimeManager.subscribeToRooms(names, (room, payload) => {
        const msg = payload?.payload as { senderId?: string }
        if (msg?.senderId && msg.senderId !== this.userId) {
          // Optimistic unread
          this.unreadByRoom = { ...this.unreadByRoom, [room]: true }
          this.emit()
        }
      })
    } catch {}
  }

  private emit() {
    this.listeners.forEach(l => {
      try { l(this.unreadByRoom) } catch {}
    })
  }

  async fetchUnread(force = false) {
    if (!this.userId) return
    const now = Date.now()
    if (this.inFlight) return this.inFlight
    if (!force && (now - this.lastFetch < this.throttleMs)) return Promise.resolve()
    if (force && (now - this.lastFetch < 250)) return Promise.resolve()

    const run = async () => {
      try {
        if (typeof document !== 'undefined' && document.hidden) return
        const readRooms = JSON.parse(localStorage.getItem('readRooms') || '{}')
        const readRoomsParam = encodeURIComponent(JSON.stringify(readRooms))
        const response = await fetch(`/api/chat/unread-simple?readRooms=${readRoomsParam}`)
        if (!response.ok) return
        const data = await response.json()
        const server = (data.unreadByRoom || {}) as Record<string, boolean>
        // Keep only true entries
        const serverTrueOnly = Object.fromEntries(Object.entries(server).filter(([, v]) => !!v)) as Record<string, boolean>
        // Merge with optimistic truths
        this.unreadByRoom = { ...this.unreadByRoom }
        // Ensure only true values remain
        Object.keys(this.unreadByRoom).forEach(k => { if (!this.unreadByRoom[k]) delete this.unreadByRoom[k] })
        this.unreadByRoom = { ...this.unreadByRoom, ...serverTrueOnly }
        this.emit()
      } catch {}
      finally {
        this.lastFetch = Date.now()
        this.inFlight = null
      }
    }

    this.inFlight = run()
    return this.inFlight
  }

  async markRead(roomName: string) {
    if (!roomName) return
    const canonical = this.canonicalize(roomName)
    if (!canonical) return
    if (this.unreadByRoom[canonical]) {
      const next = { ...this.unreadByRoom }
      delete next[canonical]
      this.unreadByRoom = next
      this.emit()
    }
    this.persistLocalRead(canonical)
    if (this.pendingMarkRead.has(canonical)) return
    this.pendingMarkRead.add(canonical)
    try {
      await fetch('/api/chat/mark-read', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ roomName: canonical }),
      })
    } catch (error) {
      console.error('Failed to mark conversation as read:', error)
    } finally {
      this.pendingMarkRead.delete(canonical)
    }
  }

  private onStorage = (e: StorageEvent) => {
    if (e.key === 'readRooms') this.fetchUnread()
  }

  private onLocalStorageUpdate = () => { 
    void this.fetchUnread()
  }

  private onVisibility = () => {
    if (typeof document !== 'undefined' && !document.hidden) this.fetchUnread()
  }

  private canonicalize(room: string) {
    return room.includes(':') ? room.split(':').sort().join(':') : room
  }

  private persistLocalRead(canonical: string) {
    if (typeof window === 'undefined') return
    try {
      const readRooms = JSON.parse(localStorage.getItem('readRooms') || '{}')
      readRooms[canonical] = Date.now()
      localStorage.setItem('readRooms', JSON.stringify(readRooms))
      window.dispatchEvent(new Event('localStorageUpdate'))
    } catch {}
  }
}

export const unreadStore = new UnreadStore()
